import { createVault, fetchVault, upsertVaultPage } from '@/lib/librarian/api';
import { backfillKnowledgeGraphForVault } from '@/lib/librarian/graph';

import { updateIndexPage } from '@/lib/librarian/updateIndexPage';
import { updateLogPage } from '@/lib/librarian/updateLogPage';
import type { KnowledgeBaseVault } from '@/lib/librarian/types';

export async function initializeVault(knowledgeBaseId: string) {
  let vault = await fetchVault(knowledgeBaseId);
  const isNewVault = !vault;
  if (!vault) {
    vault = await createVault(knowledgeBaseId);
  }

  await ensureDefaultPages(vault, isNewVault);
  await backfillKnowledgeGraphForVault(vault);
  return vault;
}

async function ensureDefaultPages(vault: KnowledgeBaseVault, shouldLogInitialization: boolean) {

  await Promise.all([
    upsertVaultPage({
      vaultId: vault.id,
      path: 'overview.md',
      title: 'Overview',
      pageType: 'overview',
      contentMarkdown: [
        '# Knowledge Base Overview',
        '',
        'This page captures the current synthesized state of the knowledge base.',
        '',
        '## What Librarian Maintains',
        '- Source summaries under `sources/`',
        '- Topic pages under `topics/`',
        '- Entity pages under `entities/`',
        '- Durable syntheses under `syntheses/`',
      ].join('\n'),
      summary: 'High-level synthesized overview of the knowledge base.',
      metadata: { generatedBy: 'librarian' },
    }),
    upsertVaultPage({
      vaultId: vault.id,
      path: 'sources/index.md',
      title: 'Sources Folder',
      pageType: 'overview',
      contentMarkdown: '# Sources\n\nSource-derived markdown pages live here.',
      summary: 'Folder landing page for source summaries.',
      metadata: { generatedBy: 'librarian' },
    }),
    upsertVaultPage({
      vaultId: vault.id,
      path: 'topics/index.md',
      title: 'Topics Folder',
      pageType: 'overview',
      contentMarkdown: '# Topics\n\nTopic synthesis pages live here.',
      summary: 'Folder landing page for topic pages.',
      metadata: { generatedBy: 'librarian' },
    }),
    upsertVaultPage({
      vaultId: vault.id,
      path: 'entities/index.md',
      title: 'Entities Folder',
      pageType: 'overview',
      contentMarkdown: '# Entities\n\nEntity-oriented pages live here.',
      summary: 'Folder landing page for entity pages.',
      metadata: { generatedBy: 'librarian' },
    }),
    upsertVaultPage({
      vaultId: vault.id,
      path: 'syntheses/index.md',
      title: 'Syntheses Folder',
      pageType: 'overview',
      contentMarkdown: '# Syntheses\n\nDurable answer pages and saved syntheses live here.',
      summary: 'Folder landing page for syntheses.',
      metadata: { generatedBy: 'librarian' },
    }),
  ]);

  await updateIndexPage(vault);
  if (shouldLogInitialization) {
    await updateLogPage(vault, 'Initialized Librarian vault structure.');
  }
}
